import React, { useRef, useState, useMemo, forwardRef, useImperativeHandle, ForwardRefExoticComponent, PropsWithoutRef, RefAttributes, useEffect } from 'react';
import { Form, Input, FormInstance, Row, Col, Collapse, Button, Space } from 'antd';
import type { SPFormProps, Column, SPFormRef } from './types';
import { FormFrom, FormMode } from './types';
import _ from 'lodash';
import SPField from './SPField';
import { useAsyncEffect } from 'ahooks';

const { Panel } = Collapse;

const SPForm = forwardRef<SPFormRef, SPFormProps>((props, ref) => {
  const {
    columns = [],
    mode = FormMode.Edit,
    isOpenGroup = false,
    from = FormFrom.Form,
    value,
    rowProps,
    colProps,
    labelCol,
    onValuesChange,
    defaultValue,
    request,
    params,
    submitter = true,
    tailLayout,
    onFinish,
    isMobile,
    ...formProps
  } = props;
  const [form] = Form.useForm();
  const [formValueByReq, setFormValueByReq] = useState();
  const valueRef = useRef<any>(value || {}); // 用于存储全量数据，包括非表单内字段

  const getFieldsValue = () => {
    return { ...value, ...valueRef.current, ...form.getFieldsValue() };
  };
  const setFieldValue = (key: any, v: any) => {
    if (key) {
      _.set(valueRef.current, key, v); // 未在表单内的字段临时存储
      form.setFieldValue(key, v);
    }
  };
  const setFieldsValue = (values: any) => {
    if (!values) {
      return;
    }
    const currentValues = form.getFieldsValue();
    form.setFieldsValue(values);
    valueRef.current = { ...values };
    onValuesChange && onValuesChange(values, { ...value, ...currentValues, ...values });
  };

  useImperativeHandle(ref, () => ({
    ...form,
    getFieldsValue,
    setFieldValue,
    setFieldsValue,
  }));

  useEffect(() => {
    if (_.isObject(defaultValue)) {
      form.setFieldsValue(defaultValue);
      valueRef.current = { ...defaultValue };
    }
  }, [JSON.stringify(defaultValue)]);


  useAsyncEffect(async () => {
    if (request) {
      const res = await request?.(params);
      res && form.setFieldsValue(res);
      setFormValueByReq(res);
    }
  }, [JSON.stringify(params)]);

  useEffect(() => {
    if (_.isObject(value)) {
      valueRef.current = { ...valueRef.current, ...value };
    }
  }, [value]);


  const itemRender = (cols: Column[]) => {
    return (
      <>
        {(cols || []).map((col) => (
          <Col className={col.moduleField.kind === 'File' ? 'cui-lcp-form-file-col' : ''} style={{ display: col.onlySaveData ? 'none' : 'block' }} span={24} {...colProps} {...(col.colProps || {})}>
            <SPField
              {...col}
              label={col.label || col.title}
              name={col.name || col.dataIndex}
              form={{
                ...form,
                setFieldValue,
                getFieldsValue,
              }}
              from={from}
              formValue={value || formValueByReq || {}}
              render={mode === FormMode.Edit ? col.renderFormItem : (col.render || ((field, config) => config.value))}
              required={mode === FormMode.Edit ? col.required : false}
              labelCol={labelCol}
              mode={mode}
            />
          </Col>
        ))}
      </>
    );
  };

  const onReset = async () => {
    form.resetFields();
    if (request) {
      const res = await request?.(params);
      res && form.setFieldsValue(res);
    }
  };

  return (
    <Form
      {...formProps}
      form={form}
      wrapperCol={{ span: 24 }}
      onValuesChange={(changeValue, allValues) => {
        onValuesChange && onValuesChange(changeValue, getFieldsValue());
      }}
      onFinish={onFinish}
    >
      {!isOpenGroup &&
        <Row gutter={8} {...rowProps}>{itemRender(columns as Column[])}</Row>
      }
      {isOpenGroup &&
        <Collapse
          bordered={false}
          expandIconPosition="end"
          defaultActiveKey={columns.map((v) => v[0])}
        >
          {columns.map(([title, cols]) => (
            <Panel header={title || ''} key={title} >
              <Row gutter={8} style={{ width: '100%' }}>
                {_.isArray(cols) && itemRender(cols)}
                {React.isValidElement(cols) && cols}
              </Row>
            </Panel>
          ))}
        </Collapse>
      }
      {submitter && mode === FormMode.Edit &&
        <Form.Item {...tailLayout}>
          <Space>
            <Button type="primary" htmlType="submit">
              提交
            </Button>
            <Button htmlType="button" onClick={onReset}>
              重置
            </Button>
          </Space>
        </Form.Item>
      }
    </Form >
  );
});

export default SPForm;
